// Stack — curated interview problems with multiple worked approaches.
// Same shape as dsaProblems/slidingWindow.js: concise steps, a "why & when to use" close per
// approach, and an interactive visualizer clarifying the monotonic stack's pop/push decisions.

const stackProblems = [
  {
    id: 'daily-temperatures',
    title: 'Daily Temperatures',
    difficulty: 'Medium',
    leetcode: { number: 739, url: 'https://leetcode.com/problems/daily-temperatures/' },
    statement:
      'Given an array of daily temperatures, return an array answer where answer[i] is the number of days you\'d ' +
      'have to wait after day i for a warmer temperature. If there is no future day with a warmer temperature, ' +
      'put 0 for that day instead.',
    example: {
      input: 'temperatures = [73, 74, 75, 71, 69, 72, 76, 73]',
      output: '[1, 1, 4, 2, 1, 1, 0, 0]',
      note: 'Day 0 (73°) waits 1 day for 74°. Day 2 (75°) waits 4 days for 76°. Days 6 and 7 never see a warmer day.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Scan Forward From Every Day)',
        why:
          "The direct reading of the problem: for each day, scan forward day by day until a warmer temperature " +
          "shows up, counting how many days that took. Correct, but every day triggers its own forward scan, " +
          "even though a lot of that scanning re-examines days already looked at from an earlier starting point.",
        steps: [
          'For each day i, scan forward through every later day j.',
          'The moment temperatures[j] is warmer than temperatures[i], record j − i as the wait and stop scanning for this day.',
          'If no warmer day is ever found, record 0.',
          'Every day gets its own forward scan — O(n²) in the worst case (a strictly decreasing temperature sequence).',
        ],
        code:
`public int[] dailyTemperaturesBruteForce(int[] temperatures) {
    int[] result = new int[temperatures.length];
    for (int i = 0; i < temperatures.length; i++) {
        for (int j = i + 1; j < temperatures.length; j++) {
            if (temperatures[j] > temperatures[i]) {
                result[i] = j - i;
                break;
            }
        }
    }
    return result;
}`,
        complexity: { time: 'O(n²)', space: 'O(n) — for the result array' },
        whenToUse:
          "Only useful to confirm the problem's meaning out loud — the repeated forward scanning is exactly the " +
          "wasted work a monotonic stack eliminates by resolving several days' answers the moment a warmer day arrives.",
      },
      {
        name: 'Approach 2 — Optimized: Monotonic Decreasing Stack',
        why:
          "Keep a stack of day-indices still WAITING for a warmer day, with temperatures decreasing from bottom " +
          "to top. The moment a new day's temperature beats the day on top of the stack, that new day IS the " +
          "answer for the popped day — and possibly for several days in a row, since a single warm day can " +
          "resolve every cooler day still waiting beneath it on the stack.",
        steps: [
          'Walk the days left to right, keeping a stack of indices whose warmer day hasn\'t been found yet.',
          'While today\'s temperature is warmer than the temperature at the index on top of the stack, pop that index and record today\'s index minus the popped index as its wait.',
          'Push today\'s index — its own answer isn\'t known yet.',
          'Any index still on the stack when the scan ends never found a warmer day — its answer stays 0.',
        ],
        visual: {
          arrays: [{ label: 'temperatures', values: [73, 74, 75, 71, 69, 72, 76, 73] }, { label: 'stack (indices, temps shown)', values: [] }],
          varNames: [],
          steps: [
            { highlights: { temperatures: [0] }, snapshots: { 'stack (indices, temps shown)': [73] }, note: 'Day 0 (73°): stack empty — push. stack = [73].' },
            { highlights: { temperatures: [1] }, snapshots: { 'stack (indices, temps shown)': [74] }, note: 'Day 1 (74°): warmer than the stacked 73° — pop day 0, its wait = 1−0 = 1. Push day 1. stack = [74].' },
            { highlights: { temperatures: [2] }, snapshots: { 'stack (indices, temps shown)': [75] }, note: 'Day 2 (75°): warmer than 74° — pop day 1, wait = 2−1 = 1. Push day 2. stack = [75].' },
            { highlights: { temperatures: [3, 4] }, snapshots: { 'stack (indices, temps shown)': [75, 71, 69] }, note: 'Days 3 (71°) and 4 (69°) are both cooler than the top (75°) — just push both. stack = [75, 71, 69].' },
            { highlights: { temperatures: [5] }, snapshots: { 'stack (indices, temps shown)': [75, 72] }, note: 'Day 5 (72°): warmer than 69° — pop day 4, wait = 5−4 = 1. Also warmer than 71° — pop day 3, wait = 5−3 = 2. Not warmer than 75° — stop. Push day 5. stack = [75, 72].' },
            { highlights: { temperatures: [6] }, snapshots: { 'stack (indices, temps shown)': [76] }, note: 'Day 6 (76°): warmer than 72° — pop day 5, wait = 6−5 = 1. Also warmer than 75° — pop day 2, wait = 6−2 = 4. Push day 6. stack = [76].' },
            { highlights: { temperatures: [7] }, snapshots: { 'stack (indices, temps shown)': [76, 73] }, note: 'Day 7 (73°): not warmer than 76° — just push. Scan ends with days 6 and 7 still on the stack — their answers stay 0. Final: [1, 1, 4, 2, 1, 1, 0, 0].' },
          ],
        },
        code:
`public int[] dailyTemperatures(int[] temperatures) {
    int[] result = new int[temperatures.length];
    Deque<Integer> stack = new ArrayDeque<>();  // indices, temperatures decreasing

    for (int i = 0; i < temperatures.length; i++) {
        while (!stack.isEmpty() && temperatures[stack.peek()] < temperatures[i]) {
            int prevDay = stack.pop();
            result[prevDay] = i - prevDay;
        }
        stack.push(i);
    }
    return result;
}`,
        complexity: { time: 'O(n)', space: 'O(n)' },
        interviewNote: "Try the \"Next Greater Element\" mode in this pattern's interactive playground — it's the exact same stack mechanic, just recording the next bigger VALUE instead of the DISTANCE to it.",
        whenToUse:
          "This is the flagship monotonic-stack problem — whenever a question asks for \"next warmer/greater/taller " +
          "thing to the right\" for every position at once, this decreasing-stack skeleton is the answer, and it " +
          "generalizes directly to Next Greater Element and Largest Rectangle in a Histogram.",
      },
    ],
  },
];

export default stackProblems;
